import React, { useRef, useState } from 'react'
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  View,
} from 'react-native'
import {
  BG_STYLES,
  BORDER_STYLES,
  CHIP_STATES,
  CHIP_TYPES,
  Chip,
  COLORS,
  COMMON_STYLES,
  FilterIcon,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import BouncyCheckbox from 'react-native-bouncy-checkbox'
import { RoleType } from 'app/constants/request'
import { useTranslation } from 'react-i18next'
import RemovableFilterChip from 'app/components/reusable/RemovableFilterChip'

type AppliedFilters = {
  created_at_after?: string
  created_at_before?: string
  status?: string
  request_type?: string
  [key: string]: any
}

type Props = {
  styles: any
  statusFilters: string[]
  statusFilter: string
  appliedFilters: AppliedFilters
  roleType?: string
  selectableRequestIds: number[]
  isMultiSelectEnabled: boolean
  isSelectAllActive: boolean
  onOpenFilter: () => void
  onChangeStatus: (status: string) => void
  onToggleSelectAll: () => void
  onToggleMultiSelect: (value: boolean) => void
  onRemoveFilter: (key: string) => void
}

const KEYS_HIDDEN_FROM_CHIPS = new Set(['status', 'created_at_before', 'created_at_after'])

const formatFilterValue = (val: unknown): string => {
  if (val == null) return ''
  if (Array.isArray(val)) {
    return val
      .map(item => {
        if (item == null) return ''
        if (typeof item === 'string' || typeof item === 'number') return String(item)
        if (typeof item === 'object') {
          const obj = item as Record<string, unknown>
          return String(obj.name ?? obj.label ?? obj.value ?? obj.id ?? '')
        }
        return String(item)
      })
      .filter(Boolean)
      .join(', ')
  }
  if (typeof val === 'object') {
    const obj = val as Record<string, unknown>
    return String(obj.name ?? obj.label ?? obj.value ?? obj.id ?? '')
  }
  return String(val)
}

const DemoMaterialRequestFilterHeader = ({
  styles,
  statusFilters,
  statusFilter,
  appliedFilters,
  roleType,
  selectableRequestIds,
  isMultiSelectEnabled,
  isSelectAllActive,
  onOpenFilter,
  onChangeStatus,
  onToggleSelectAll,
  onToggleMultiSelect,
  onRemoveFilter,
}: Props) => {
  const { t } = useTranslation()
  const [showStatusMenu, setShowStatusMenu] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<{
    top: number
    left: number
    width: number
  } | null>(null)
  const statusChipRef = useRef<View | null>(null)

  const openStatusMenu = () => {
    if (statusChipRef.current) {
      statusChipRef.current.measureInWindow((x, y, w, h) => {
        setMenuAnchor({ top: y + h + 4, left: x, width: w })
        setShowStatusMenu(true)
      })
    } else {
      setMenuAnchor({ top: 60, left: 16, width: 160 })
      setShowStatusMenu(true)
    }
  }

  const handleSelectStatus = (status: string) => {
    onChangeStatus(status)
    setShowStatusMenu(false)
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'Pending':
        return t('demo_status_pending')
      case 'Approved':
        return t('demo_status_approved')
      case 'Rejected':
        return t('demo_status_rejected')
      default:
        return status
    }
  }

  return (
    <View style={[styles.headerWrap, BG_STYLES.bgNeutral10]}>
      <ScrollView
        style={[COMMON_STYLES.row, styles.filterRow]}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.filterContent}
      >
        <Pressable
          style={[
            BORDER_STYLES.borderNeutral20,
            COMMON_STYLES['br1/2'],
            COMMON_STYLES['mr0.5'],
            BG_STYLES.bgWhite,
            styles.filterBtn,
          ]}
          onPress={onOpenFilter}
        >
          <FilterIcon
            svgProps={{
              height: 22,
              width: 22,
              fill: COLORS.neutral70,
            }}
          />
        </Pressable>

        <View ref={statusChipRef} collapsable={false}>
          <Chip
            label={getStatusLabel(statusFilter)}
            type={CHIP_TYPES.SUGGESTIVE}
            state={CHIP_STATES.ACTIVE}
            onClick={openStatusMenu}
            activeContainerStyle={[BG_STYLES.bgBlack, { borderColor: COLORS.black }]}
            labelStyle={{ color: COLORS.white }}
            iconStrokeColor="white"
          />
        </View>

        {Object.entries(appliedFilters).map(([key, val]) => {
          if (KEYS_HIDDEN_FROM_CHIPS.has(key)) return null
          const label = formatFilterValue(val)
          if (!label) return null
          return (
            <RemovableFilterChip
              key={`filter_${key}`}
              label={label}
              onRemove={() => onRemoveFilter(key)}
            />
          )
        })}

        {appliedFilters.created_at_before && appliedFilters.created_at_after && (
          <RemovableFilterChip
            key="date_range"
            label={t('demo_date_range_label', {
              from: appliedFilters.created_at_before,
              to: appliedFilters.created_at_after,
            })}
            onRemove={() => onRemoveFilter('date_range')}
          />
        )}
      </ScrollView>

      {(roleType === RoleType.L1 || roleType === RoleType.L2) &&
        statusFilter === statusFilters[0] &&
        selectableRequestIds.length > 0 && (
          <View style={[COMMON_STYLES.row, COMMON_STYLES.justifyBetween, styles.multiRow]}>
            <View style={COMMON_STYLES.flex1}>
              {isMultiSelectEnabled && (
                <Pressable onPress={onToggleSelectAll}>
                  <BouncyCheckbox
                    isChecked={isSelectAllActive}
                    onPress={onToggleSelectAll}
                    size={20}
                    fillColor={COLORS.primary100}
                    iconStyle={styles.checkbox}
                    innerIconStyle={styles.checkbox}
                    text={isSelectAllActive ? t('demo_unselect_all') : t('demo_select_all')}
                    textStyle={[styles.selectAllText, { textDecorationLine: 'none' }]}
                  />
                </Pressable>
              )}
            </View>
            <View style={[COMMON_STYLES.row, COMMON_STYLES.alignItemsCenter, { gap: 8 }]}>
              <Text style={styles.selectAllText}>{t('demo_select_multiple')}</Text>
              <Switch
                trackColor={{ false: COLORS.white, true: COLORS.success10 }}
                thumbColor={isMultiSelectEnabled ? COLORS.primary120 : COLORS.neutral70}
                onValueChange={onToggleMultiSelect}
                value={isMultiSelectEnabled}
              />
            </View>
          </View>
        )}

      <Modal
        transparent
        visible={showStatusMenu}
        animationType="none"
        onRequestClose={() => setShowStatusMenu(false)}
      >
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={() => setShowStatusMenu(false)}
        />
        {menuAnchor && (
          <View
            style={[
              menuStyles.menu,
              {
                top: menuAnchor.top,
                left: menuAnchor.left,
                minWidth: Math.max(menuAnchor.width, 160),
              },
            ]}
          >
            {statusFilters.map(status => {
              const isSelected = status === statusFilter
              return (
                <Pressable
                  key={status}
                  style={({ pressed }) => [
                    menuStyles.menuRow,
                    pressed && menuStyles.menuRowPressed,
                  ]}
                  onPress={() => handleSelectStatus(status)}
                >
                  <View
                    style={[
                      menuStyles.radioOuter,
                      isSelected && menuStyles.radioOuterSelected,
                    ]}
                  >
                    {isSelected && <View style={menuStyles.radioInner} />}
                  </View>
                  <Text
                    style={[
                      TYPOGRAPHY.TEXT2,
                      VARIANTS_STYLES.BASE,
                      TEXT_STYLES.textNeutral90,
                      menuStyles.menuLabel,
                      isSelected && VARIANTS_STYLES.SEMI_EMPHASIZED,
                    ]}
                  >
                    {getStatusLabel(status)}
                  </Text>
                </Pressable>
              )
            })}
          </View>
        )}
      </Modal>
    </View>
  )
}

const menuStyles = StyleSheet.create({
  menu: {
    position: 'absolute',
    backgroundColor: COLORS.white,
    borderRadius: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: COLORS.neutral20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 6,
  },
  menuRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    columnGap: 12,
  },
  menuRowPressed: {
    backgroundColor: COLORS.neutral10,
  },
  menuLabel: {
    flex: 1,
  },
  radioOuter: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    borderColor: COLORS.neutral30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOuterSelected: {
    borderColor: COLORS.primary100,
  },
  radioInner: {
    width: 9,
    height: 9,
    borderRadius: 5,
    backgroundColor: COLORS.primary100,
  },
})

export default DemoMaterialRequestFilterHeader
